import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { ShieldCheck, AlertTriangle, Download, Newspaper, Eye, Trophy } from "lucide-react";

export default function ReporterCertVerifyPage() {
  const { cid } = useParams();
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    api.get(`/reporter-rewards/verify/${cid}`)
      .then((r) => setState({ loading: false, data: r.data }))
      .catch(() => setState({ loading: false, data: { valid: false, message: "Verification failed" } }));
  }, [cid]);

  return (
    <>
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-4 py-10 pb-24" data-testid="cert-verify-page">
        <div className="text-center mb-6">
          <div className="tag-cat inline-block mb-3">Certificate Verification</div>
          <h1 className="font-devnagri-heading text-3xl md:text-4xl font-black text-[#0F172A]">प्रमाण-पत्र सत्यापन</h1>
          <div className="text-xs text-slate-500 font-mono mt-2 break-all" data-testid="cert-id">ID: {cid}</div>
        </div>

        {state.loading ? <div className="text-center py-10 text-slate-500">Verifying…</div> :
         !state.data?.valid ? (
          <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-6 text-center" data-testid="cert-invalid">
            <AlertTriangle className="w-14 h-14 text-red-500 mx-auto mb-3"/>
            <div className="font-devnagri-heading text-xl font-black text-red-800">यह प्रमाण-पत्र मान्य नहीं है</div>
            <div className="text-sm text-red-700 mt-2">{state.data?.message || "Certificate ID हमारे records में नहीं मिली।"}</div>
          </div>
         ) : (
          <div className="rounded-2xl border-2 border-emerald-300 bg-white shadow-xl overflow-hidden" data-testid="cert-valid">
            <div className="bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 text-white p-5 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <ShieldCheck className="w-6 h-6"/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-black text-emerald-100">Verified · Authentic</div>
                <div className="font-devnagri-heading text-lg font-black leading-tight">यह प्रमाण-पत्र सत्यापित है ✓</div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center text-3xl">🏆</div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-black text-amber-600">Reporter</div>
                  <div className="font-devnagri-heading text-2xl font-black text-[#0F172A] leading-tight">{state.data.reporter_name}</div>
                  <div className="text-sm text-slate-500">{state.data.designation}{state.data.district ? ` · ${state.data.district}` : ""}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center">
                  <Trophy className="w-5 h-5 text-amber-600 mx-auto"/>
                  <div className="font-devnagri-heading font-black text-slate-800 mt-1">{state.data.rank === 1 ? "प्रथम" : state.data.rank === 2 ? "द्वितीय" : state.data.rank === 3 ? "तृतीय" : `${state.data.rank}वाँ`}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">स्थान</div>
                </div>
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-center">
                  <Newspaper className="w-5 h-5 text-red-600 mx-auto"/>
                  <div className="font-black text-slate-800 mt-1">{state.data.story_count}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">समाचार</div>
                </div>
                <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-center">
                  <Eye className="w-5 h-5 text-sky-600 mx-auto"/>
                  <div className="font-black text-slate-800 mt-1 tabular-nums">{Number(state.data.total_views).toLocaleString("hi-IN")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">पाठक</div>
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">प्रदान की अवधि</div>
                  <div className="font-bold text-slate-800">{state.data.period}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">जारी तिथि</div>
                  <div className="font-bold text-slate-800">{state.data.issued_at_hindi}</div>
                </div>
              </div>
              {state.data.pdf_url && (
                <a href={state.data.pdf_url} target="_blank" rel="noopener noreferrer"
                   className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 via-orange-600 to-rose-600 hover:from-amber-600 hover:to-rose-700 text-white font-black text-sm flex items-center justify-center gap-2 shadow-lg"
                   data-testid="cert-download">
                  <Download className="w-4 h-4"/>Certificate PDF Download करो
                </a>
              )}
              <p className="text-[11px] text-slate-500 text-center">यह प्रमाण-पत्र प्रधान संपादक <b>JAKIR MOMIN</b> द्वारा digitally जारी किया गया है।</p>
            </div>
          </div>
         )}
      </main>
      <PublicFooter />
    </>
  );
}
